/**
 * Scheduling graph builder.
 *
 * Implements `05-` §B.1 and `04-` §13/§14 (move_projects: true/false rules).
 *
 * Phase A scope: structural builder only. Engine pass logic that consumes the
 * graph (forward/latest passes, summary translation per D-08, fixed-point
 * loop per §16) lives in `core/planner/` (Phase C).
 *
 * Decisions surfaced here:
 *  - **D-08** — every descendant of a translated summary moves by delta;
 *    constraint violations surface as Conflict. The graph builder itself is
 *    not where translation runs (that's Phase C), but containment edges are
 *    emitted only under moveProjects:true since they drive translation.
 *  - **D-10** — `SchedEdge` carries per-component calendars via normalizeLink.
 *  - **D-11** — adapter is responsible for filtering unscheduled tasks; the
 *    builder is defensive (skips tasks where `unscheduled === true` even if
 *    they slip through).
 *  - **D-05** — `SchedNode.excluded = true` for any task in
 *    `SchedulingConfig.draggedTaskIds`.
 *  - **D-34** — anchor dates are stored as numeric epoch timestamps; the
 *    builder converts from `Snapshot`'s `Date` instances here.
 */

import type { Snapshot, NormalizedTaskID, ConstraintType } from "../types";
import type { SchedulingGraph, SchedNode, SchedEdge, ContainmentEdge } from "./types";
import { normalizeLink } from "./normalize_link";

/**
 * Is leaf `d` held in place by its OWN hard constraint such that it does NOT
 * free-flow with its project — and therefore must not define the project's
 * "movable front" (the block's resolve anchor / delta reference in
 * `schedule_pass.ts`, and the project-shape offset baseline in the explode
 * branch below)?
 *
 * Resolved-INDEPENDENT (compares constraint kind only, not dates), so the same
 * predicate drives both consumers with no circularity:
 *   - Forward (the block's START is the reference): exclude leaves whose
 *     constraint can pin them EARLIER than the free flow — exact pins
 *     (MSO/MFO) and upper-bound caps (FNLT/SNLT). Lower bounds (SNET/FNET) only
 *     push later, so they never sit before the free-flow head.
 *   - Backward (the block's END is the reference): symmetric — exclude exact
 *     pins and lower-bound floors (SNET/FNET); upper bounds (FNLT/SNLT) never
 *     sit after the free-flow tail.
 *
 * The free-flowing leaves (unconstrained / soft / opposite-bound) define where
 * the block's movable head/tail actually is, so offsets measured from the
 * movable front collapse to a stable fixpoint once the free front aligns with
 * its link landing.
 */
export function excludedFromMovableBound(d: SchedNode, direction: "forward" | "backward"): boolean {
  const ct = d.constraintType;
  if (!ct || d.constraintDateTs === undefined) return false;
  if (ct === "mso" || ct === "mfo") return true; // exact pins never free-flow
  return direction === "forward"
    ? ct === "fnlt" || ct === "snlt"   // upper-bound caps can pin a leaf early
    : ct === "snet" || ct === "fnet";  // lower-bound floors can pin a leaf late
}

/**
 * Build a SchedulingGraph from an immutable Snapshot.
 *
 * The unmemoized form: every call constructs a fresh graph. The exported
 * `buildSchedulingGraph` in `core/api.ts` is the memoized wrapper.
 */
export function buildSchedulingGraph_unmemoized(snapshot: Snapshot): SchedulingGraph {
  const cfg = snapshot.config;
  // Per `04-` §16.5 + D-08: `gap_behavior: "compress"` is incompatible
  // with the D-08 bulk-translation model — compress wants every leaf at
  // its earliest feasible start, which means summaries can't translate
  // their descendants as a group. When compress is active we behave as
  // if `move_projects: false`: summaries become non-scheduling nodes and
  // summary-targeted links are exploded to leaf-to-leaf edges via
  // `expandToLeaves`. Compress wins over moveProjects per the design.
  const moveProjects = cfg.moveProjects && cfg.gapBehavior !== "compress";
  const draggedTaskIds = cfg.draggedTaskIds ?? new Set<NormalizedTaskID>();

  // ── Nodes ──────────────────────────────────────────────────────────
  const nodes = new Map<NormalizedTaskID, SchedNode>();
  for (const [id, task] of snapshot.tasks) {
    // D-11: defensive — adapter should have filtered, but skip just in case.
    if (task.unscheduled) continue;
    // D-12 / D-25 etc. — the adapter has already filtered per its rules; the
    // engine accepts what it's given.

    // moveProjects: false ⇒ summaries are not scheduling nodes (their dates
    // are derived from descendants for display only). Skip them in the
    // scheduling graph.
    if (!moveProjects && task.kind === "summary") continue;

    // Per `04-` §16.5: anchor is either the live bbox (TaskSnapshot dates)
    // or the override from anchorOverrides (drag/edit pre-mutation case).
    const override = snapshot.anchorOverrides.get(id);
    const anchorStartTs = (override ? override.start : task.startDate).getTime();
    const anchorEndTs   = (override ? override.end   : task.endDate).getTime();

    // GS-2083 / GS-3328: preferred-date mode (`apply_constraints: false`)
    // schedules from links and anchors only — the task's constraint fields
    // (MSO/MFO/SNET/…, and explicit asap/alap) must not influence the plan.
    // Per `04-` §11(2) (16-cell matrix) and §16, classification falls back to
    // the direction default. Dropping the fields HERE (node construction) is
    // the single choke point for every planner-side consumer: the asap/alap
    // bound clamps, `excludedFromMovableBound`, the secondary ALAP sub-pass
    // in `schedule_pass.ts`, and the option-A descendant clamp in
    // `summary_translation.ts`. The analysis graph is built separately and
    // keeps constraints regardless of mode flags (D-16).
    const constraintType: ConstraintType = (cfg.applyConstraints ? task.constraintType : undefined)
      ?? defaultConstraintType(cfg.scheduleFromEnd);
    const constraintDateTs = cfg.applyConstraints
      ? task.constraintDate?.getTime()
      : undefined;

    // Phase D Day 9: completed-task flag (D-12). When useProgress: true AND
    // task.progress === 1, the planner treats this node as a fixed-date
    // node (same as excluded). Skips resolveAsap/Alap for the node.
    const isCompleted = cfg.useProgress === true && task.progress === 1;

    nodes.set(id, {
      id,
      kind: task.kind,
      calendar: snapshot.calendars.resolveForTask(id),
      constraintType,
      constraintDateTs,
      duration: task.duration,
      durationUnit: task.durationUnit,
      anchorStartTs,
      anchorEndTs,
      excluded: draggedTaskIds.has(id) || !task.autoScheduling,
      isCompleted
    });
  }

  // ── Edges ──────────────────────────────────────────────────────────
  // Pre-compute the set of tasks that already have at least one DIRECT
  // incoming link (i.e., a link whose `target` is this task — not a link
  // whose target is an ancestor summary). v1's `_getImplicitLinks` skips the
  // project-shape offset for any leaf whose `$target.length > 0` because that
  // leaf is already positioned by its own predecessor — applying a project-
  // shape offset on top would double-count the constraint. Test
  // 11_auto_scheduling/015 exposes this: milestone 32 has its own incoming
  // links 1→32 and 2→32, AND is a leaf of project 3 which is targeted by
  // link 2→3. Without the skip, v2's expansion of 2→3 produced an edge
  // 21→32 with extraLag = (32.start − project3.start) > 0, pushing the
  // milestone past `12-04`.
  const tasksWithOwnIncomingLink = new Set<NormalizedTaskID>();
  for (const link of snapshot.links) {
    if (snapshot.tasks.has(link.target)) {
      tasksWithOwnIncomingLink.add(link.target);
    }
  }

  const edges: SchedEdge[] = [];
  for (const link of snapshot.links) {
    const sourceTask = snapshot.tasks.get(link.source);
    const targetTask = snapshot.tasks.get(link.target);
    if (!sourceTask || !targetTask) continue;
    // Per D-11: links to/from unscheduled tasks should already be filtered by
    // the adapter; defensive skip.
    if (sourceTask.unscheduled || targetTask.unscheduled) continue;

    if (moveProjects) {
      // Summary endpoints stay summary endpoints; no explosion.
      // Both endpoints must be in `nodes` (a summary node may not appear if
      // adapter filtering removed it — defensive guard).
      if (!nodes.has(link.source) || !nodes.has(link.target)) continue;
      edges.push(normalizeLink(link, sourceTask, targetTask, snapshot.calendars));
    } else {
      // A manually-scheduled summary (`auto_scheduling: false`) carries its
      // own dates and is intentionally excluded from the schedule (D-05). v1's
      // `_getImplicitLinks` short-circuits in this case and returns the summary
      // itself as the only endpoint — and since the summary node is excluded,
      // the link ends up effectively dead. Mirror that here by dropping the
      // link entirely when either endpoint is a manually-scheduled summary;
      // otherwise the explosion to leaves would propagate the link to the
      // summary's children and pull them around (test 036 GS-`auto_scheduling:
      // false` project: link 2→5 with task 5 excluded was leaking to tasks
      // 6/7 and dragging them onto the predecessor's end).
      const isSourceManualSummary = sourceTask.kind === "summary" && sourceTask.autoScheduling === false;
      const isTargetManualSummary = targetTask.kind === "summary" && targetTask.autoScheduling === false;
      if (isSourceManualSummary || isTargetManualSummary) continue;

      // moveProjects: false → explode summary endpoints into leaves.
      let sources = expandToLeaves(snapshot, link.source);
      const targets = expandToLeaves(snapshot, link.target);

      // GS-2588 / GS-2014: SS / SF links anchor the target on the SOURCE's
      // START. For a summary source that start is the project's start = the
      // EARLIEST child's start. Exploding to a per-child edge for every child
      // and letting the pass take asap's `max()` would instead anchor on the
      // LATEST child (the project's end-most start), so an SS-to-project that
      // should fire at 25-03 fired at 29-03 (test 73, both v1 and v2). FS / FF
      // anchor on the source's END (= max child end), where asap's `max()` over
      // all children is exactly right — so restrict this only to SS / SF, and
      // emit the single edge from the earliest-starting leaf that defines the
      // summary's start. (`expandToLeaves` returns the leaf itself for a
      // non-summary source, so this is a no-op there.)
      const sourceAnchorsOnStart = link.type === "ss" || link.type === "sf";
      if (sourceAnchorsOnStart && sourceTask.kind === "summary" && sources.length > 1) {
        let earliest: NormalizedTaskID | undefined;
        let earliestTs = Number.POSITIVE_INFINITY;
        for (const s of sources) {
          if (!nodes.has(s)) continue;
          const st = snapshot.tasks.get(s);
          if (!st) continue;
          const ts = st.startDate.getTime();
          if (ts < earliestTs) { earliestTs = ts; earliest = s; }
        }
        if (earliest !== undefined) sources = [earliest];
      }
      // GS-2784: when the original target is a summary, each leaf descendant
      // carries an extra lag equal to its OFFSET from the summary's start,
      // measured in the SUMMARY's calendar. Mirrors v1's `_getImplicitLinks`
      // `selectOffset` (target side), which preserves the project's "shape"
      // — leaves stay at their relative positions inside the project even
      // when the link compresses the project's earliest leaf to the source's
      // end. Without it, every leaf gets pulled to the source's end and the
      // project collapses to a single point (Tasks 4, 7, 8 in test 28 GS-2784
      // case 1 all landed on Task 1's end instead of staying at their
      // anchors). Source-side offset is 0 under forward scheduling per v1's
      // `selectOffsetFrom`; only the target side matters here.
      //
      // v1 parity (`links_common.js:73-76`): `respectTargetOffset =
      // move_projects`, additionally dropped when `apply_constraints +
      // gap_behavior:"compress"`.
      //  - The `move_projects` factor was previously missing. Under
      //    `move_projects:false` v1 applies NO project-shape offset — a
      //    summary→leaf link collapses every leaf onto the predecessor end
      //    (the project loses its shape on purpose). Omitting the factor made
      //    v2 keep the offset, so `move_projects:false` drags spread the
      //    subtasks out instead of stacking them. Test 027 steps 3-4
      //    (`move_projects:false`) assert the collapse (21=22=23 all land on
      //    the predecessor end).
      //  - The `apply_constraints + compress` drop is retained: there the
      //    project's "shape" is intentionally collapsed (every leaf wants its
      //    earliest feasible start). Test 053 step 2 depends on this — with the
      //    offset active, milestone 16's longest predecessor came via task 23's
      //    +9d offset push; suppressing it lets the milestone follow the chain
      //    end (task 21 / 22) directly.
      const isTargetSummary = targetTask.kind === "summary";
      const respectTargetOffset =
        cfg.moveProjects && !(cfg.applyConstraints && cfg.gapBehavior === "compress");
      const targetSummaryCal = isTargetSummary && respectTargetOffset
        ? snapshot.calendars.resolveForTask(link.target)
        : null;
      // Offset anchor — the project "front" the leaf offsets are measured from.
      // The leaves are placed at `predEnd + offset`, so the offset is idempotent
      // (a leaf reproduces its own date) only when `anchor == predEnd` (the link
      // landing). Two regimes:
      //
      //  • No anchor-override on the target summary (no mutation, or only the
      //    SOURCE moved): use the live MOVABLE front — the min start over the
      //    project's constraint-UNPINNED leaves (`excludedFromMovableBound`,
      //    shared with the block path's `movableBound`). When the source moves,
      //    the whole target block translates from this front (retest 027 step 1);
      //    on a converged compress the free front sits at the link landing, so
      //    the offsets reproduce every leaf's date (stable fixpoint).
      //
      //    Using the FULL aggregate front instead (min over ALL leaves) drifts
      //    when a constraint-pinned leaf defines the aggregate: GS-2675 — FF
      //    link to a project whose MSO child (27-10) pins the aggregate start
      //    while the free sibling is link-driven later, so the sibling's offset
      //    `wd(aggStart, sibling.live)` GREW on every `autoSchedule()` call and
      //    the sibling marched into the future without bound (sample
      //    12_auto_scheduling_027_forward). Measured from the movable front the
      //    free front's own offset is 0 by construction, it lands on the raw
      //    link landing, and the next call reproduces it — idempotent for any
      //    link type. Pinned leaves get clamp-0 offsets and are then clamped to
      //    their own constraint anyway (option-A semantics). Falls back to the
      //    full aggregate front when EVERY leaf is pinned.
      //
      //  • Anchor-override PRESENT ⇒ a descendant of THIS target project moved
      //    during the mutation window (its aggregate changed). The pre-mutation
      //    front is then the dragged leaf's own (already shifted) start; measuring
      //    offsets from it drags every sibling along — a front leaf dragged forward
      //    shrinks each sibling's offset by the drag distance, and since the anchor
      //    cache re-captures the already-shifted front at the next drag's start,
      //    siblings creep one working day per consecutive drag (retest 089;
      //    identical on the v1 engine — a shared limitation, not a v2 regression).
      //    Re-pin the anchor to the LINK LANDING (`sourceTask.endDate`) — the same
      //    point the pass resolves the leaves against — so `anchor == predEnd` and
      //    siblings stay idempotent regardless of how (or how many times) the
      //    dragged descendant moves. In the converged-compress case the front
      //    already equals the landing, so this is a no-op (e.g. retest 027 step 2).
      //
      // Why the override's PRESENCE (not `draggedTaskIds`) is the discriminator:
      // `tasks_dnd.js:on_finish` calls `clear_drag_state()` BEFORE firing
      // `onAfterTaskDrag`, so the deferred post-drag autoSchedule sees
      // `tasksDnd.drag_id === undefined` ⇒ `draggedTaskIds` is empty here (the
      // dragged task is held by the rootTaskId+preserve floor, not `excluded`).
      // The override is captured at drag-begin and survives into the snapshot.
      const targetOverride = isTargetSummary ? snapshot.anchorOverrides.get(link.target) : undefined;
      let targetSummaryStart: Date | null = null;
      if (isTargetSummary && respectTargetOffset) {
        if (targetOverride) {
          targetSummaryStart = sourceTask.endDate;
        } else {
          // Movable front: min start over constraint-unpinned target leaves.
          const direction = cfg.scheduleFromEnd ? "backward" as const : "forward" as const;
          let movableTs = Number.POSITIVE_INFINITY;
          for (const tLeaf of targets) {
            const node = nodes.get(tLeaf);
            if (!node || excludedFromMovableBound(node, direction)) continue;
            const ts = snapshot.tasks.get(tLeaf)?.startDate.getTime();
            if (ts !== undefined && ts < movableTs) movableTs = ts;
          }
          targetSummaryStart = Number.isFinite(movableTs)
            ? new Date(movableTs)
            : targetTask.startDate; // every leaf pinned — full aggregate front
        }
      }
      for (const sLeaf of sources) {
        for (const tLeaf of targets) {
          if (sLeaf === tLeaf) continue; // skip degenerate self-edges
          if (!nodes.has(sLeaf) || !nodes.has(tLeaf)) continue;
          const sTask = snapshot.tasks.get(sLeaf)!;
          const tTask = snapshot.tasks.get(tLeaf)!;
          let extraLag = 0;
          if (
            isTargetSummary &&
            respectTargetOffset &&
            tLeaf !== link.target &&
            // v1 `_getImplicitLinks` `selectOffset`: skip the project-shape
            // offset for any leaf that already has its own direct incoming
            // link. The leaf is positioned by that link; pushing it further
            // via the summary offset would double-count.
            !tasksWithOwnIncomingLink.has(tLeaf)
          ) {
            // Offset in the summary's calendar from the (pre-mutation) summary
            // start to this leaf's start, in the link's lag unit. Clamp at 0:
            // every child sits at or after its project's start, so the offset is
            // non-negative by construction. A negative value only arises for a
            // child dragged LEFT of the pre-mutation summary start — i.e. the
            // drag target. v1 zeroes the drag target's offset explicitly
            // (`links_common.js:134` `drag_id` guard) so the moved child isn't
            // re-pinned by a project-shape lag and is free to settle at its
            // link-driven date; clamping the override-referenced offset
            // reproduces that for the moved child while leaving every sibling's
            // offset (measured from the pre-mutation start) intact.
            extraLag = Math.max(0, targetSummaryCal!.calculateDuration(
              targetSummaryStart!,
              tTask.startDate,
              link.lagUnit
            ));
          }
          const baseEdge = normalizeLink({
            ...link,
            source: sLeaf,
            target: tLeaf,
            lag: link.lag + extraLag
          }, sTask, tTask, snapshot.calendars);
          // For an exploded summary→leaf edge, the lag (project-shape offset
          // PLUS the user lag) must be APPLIED in the SUMMARY's calendar —
          // v1's `getEarliestStartDate` does `task: taskCalendar` where
          // `taskCalendar` is the target project under `move_projects &&
          // subtaskLink`. Otherwise a leaf whose own calendar differs from
          // its parent project's (e.g. fulltime 0-24 vs the project's global
          // 8-17 Mon-Fri) would convert the working-day offset into
          // calendar-day arithmetic and land on the wrong date (`28_..._GS-2784`).
          const edge = isTargetSummary && respectTargetOffset
            ? { ...baseEdge, lagCalendar: targetSummaryCal! }
            : baseEdge;
          edges.push(edge);
        }
      }
    }
  }

  // ── Containment edges (only under moveProjects:true) ───────────────
  const containment: ContainmentEdge[] = [];
  if (moveProjects) {
    for (const id of nodes.keys()) {
      if (!snapshot.hierarchy.isSummary(id)) continue;
      for (const desc of snapshot.hierarchy.descendantsOf(id)) {
        // Only emit edges into nodes that are part of the scheduling graph.
        if (nodes.has(desc)) {
          containment.push({ summary: id, descendant: desc });
        }
      }
    }
  }

  // ── Indices ────────────────────────────────────────────────────────
  const predecessors = new Map<NormalizedTaskID, SchedEdge[]>();
  const successors = new Map<NormalizedTaskID, SchedEdge[]>();
  for (const e of edges) {
    pushTo(predecessors, e.target, e);
    pushTo(successors,   e.source, e);
  }
  // Freeze inner arrays via readonly cast — they're not actually frozen at
  // runtime, but the SchedulingGraph type marks them readonly. The engine
  // never mutates these.
  const predecessorsRO = new Map<NormalizedTaskID, readonly SchedEdge[]>();
  const successorsRO = new Map<NormalizedTaskID, readonly SchedEdge[]>();
  for (const [k, v] of predecessors) predecessorsRO.set(k, v);
  for (const [k, v] of successors) successorsRO.set(k, v);

  return {
    nodes,
    edges,
    containment,
    predecessors: predecessorsRO,
    successors: successorsRO
  };
}

function defaultConstraintType(scheduleFromEnd: boolean): ConstraintType {
  return scheduleFromEnd ? "alap" : "asap";
}

function pushTo<K, V>(map: Map<K, V[]>, key: K, value: V): void {
  let arr = map.get(key);
  if (!arr) {
    arr = [];
    map.set(key, arr);
  }
  arr.push(value);
}

/**
 * For moveProjects: false explosion. Returns the leaf descendants of the
 * given task, or [taskId] if it's already a leaf.
 */
function expandToLeaves(snapshot: Snapshot, taskId: NormalizedTaskID): readonly NormalizedTaskID[] {
  if (snapshot.hierarchy.isSummary(taskId)) {
    return snapshot.hierarchy.leavesOf(taskId);
  }
  return [taskId];
}
